import { useCallback, useMemo, useState } from 'react';

import { getPredictionEventForCardConfig } from '@src/types/PredictionMarket';
import {
  Box,
  CardViewEnum,
  MarketDataCardFormWrapper,
  useSecurity,
  type PredictionMarketCardConfig,
} from '@talos/kyoko';

import { usePredictionsContext } from '@src/providers/PredictionsProvider';
import { PredictionCardForm, PredictionCardFormActions } from './PredictionCardForm';
import { buildPredictionMarketCardConfig, resolveInitialPredictionMarket } from './predictionCardMarkets';
import type { PredictionCardFormValues } from './usePredictionCardForm';

interface PredictionCardSettingsProps {
  config: PredictionMarketCardConfig;
  onClose: () => void;
  onSave: (config: PredictionMarketCardConfig) => void;
  onDelete: (config: PredictionMarketCardConfig) => void;
}

export function PredictionCardSettings({ config, onClose, onSave, onDelete }: PredictionCardSettingsProps) {
  const { eventsByID, eventSeriesByID, refDataIndexes } = usePredictionsContext();
  const configuredOutcome = useSecurity(config.outcomeSymbol);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = useMemo((): PredictionCardFormValues => {
    const event = getPredictionEventForCardConfig(config, eventsByID);
    const eventSeries = event?.SeriesID != null ? eventSeriesByID.get(event.SeriesID) : undefined;
    return {
      market: resolveInitialPredictionMarket(config.market, refDataIndexes.supportedMarkets),
      eventSeries,
      event,
      outcome: configuredOutcome,
      selectedMarketAccounts: config.marketAccounts ?? [],
      selectedAggregation: config.selectedAggregation,
      view: config.outcomeSymbol ? CardViewEnum.COMPACT : CardViewEnum.NORMAL,
    };
  }, [config, configuredOutcome, eventSeriesByID, eventsByID, refDataIndexes.supportedMarkets]);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>, form: PredictionCardFormValues, primaryDisabled: boolean) => {
      e.preventDefault();
      const { event } = form;
      if (primaryDisabled || !event) {
        return;
      }

      setIsSubmitting(true);
      onSave(buildPredictionMarketCardConfig(config, { ...form, event }));
      onClose();
    },
    [config, onClose, onSave]
  );

  return (
    <PredictionCardForm mode="settings" initialValues={initialValues} onClose={onClose}>
      {({ form, fields, primaryDisabled }) => (
        <MarketDataCardFormWrapper onSubmit={e => handleSubmit(e, form, primaryDisabled)}>
          <Box flex="1" minHeight={0} overflowX="hidden" overflowY="auto" w="100%">
            {fields}
          </Box>
          <PredictionCardFormActions
            mode="settings"
            primaryDisabled={primaryDisabled}
            isSubmitting={isSubmitting}
            onCancel={onClose}
            onDelete={() => onDelete(config)}
          />
        </MarketDataCardFormWrapper>
      )}
    </PredictionCardForm>
  );
}
